/**
 * @fileoverview
 * Modal Component - Reusable modal dialog
 * Migrated to new architecture with OnPush change detection
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  HostListener,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { DropdownService } from '../../../@core/services/dropdown.service';


@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css',
})
export class ModalComponent implements OnInit, OnDestroy, OnChanges {
  private dropdownService = inject(DropdownService);

  @Input() isOpen: boolean = false;
  @Input() title: string = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full' = 'md';
  @Input() showCloseButton: boolean = true;
  @Input() closeOnBackdropClick: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() customClass: string = '';
  @Input() contentClass: string = '';
  /** Use higher z-index (z-50) when this modal must appear above other modals. */
  @Input() elevated: boolean = false;

  @Output() onClose = new EventEmitter<void>();
  @Output() onOpen = new EventEmitter<void>();

  ngOnInit() {
    if (this.isOpen) {
      this.onOpen.emit();
      this.disableBodyScroll();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.onOpen.emit();
        this.disableBodyScroll();
      } else {
        this.enableBodyScroll();
        // Close all dropdowns when modal closes
        this.dropdownService.closeAllDropdowns();
      }
    }
  }

  ngOnDestroy() {
    this.enableBodyScroll();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    if (this.closeOnEscape && this.isOpen) {
      this.closeModal();
    }
  }

  closeModal() {
    // Close all dropdowns synchronously before closing modal
    this.dropdownService.closeAllDropdowns();
    this.onClose.emit();
    this.enableBodyScroll();
  }

  onBackdropClick(event: Event) {
    if (this.closeOnBackdropClick) {
      this.closeModal();
    }
  }

  private disableBodyScroll() {
    document.body.style.overflow = 'hidden';
  }

  private enableBodyScroll() {
    document.body.style.overflow = 'auto';
  }

  get modalSizeClass(): string {
    const sizeClasses: Record<string, string> = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      full: 'max-w-full mx-4',
    };
    return sizeClasses[this.size] || sizeClasses['md'];
  }

  get overlayZIndexClass(): string {
    return this.elevated ? 'z-50' : 'z-40';
  }
}

